/**
 * Helpers used to keep untrusted values away from DOM XSS sinks
 * (`innerHTML`, `[innerHTML]`, `document.write`, `bypassSecurityTrustHtml`).
 */

const HTML_ESCAPE_MAP: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

/** Element names that must never survive sanitization. */
const FORBIDDEN_TAGS = [
  'script',
  'style',
  'iframe',
  'frame',
  'frameset',
  'object',
  'embed',
  'applet',
  'link',
  'meta',
  'base',
  'form',
  'template',
];

/** Attributes that can execute script or load remote content. */
const FORBIDDEN_ATTRIBUTES = ['srcdoc', 'formaction', 'xlink:href', 'ping'];

const URL_ATTRIBUTES = ['href', 'src', 'action', 'background', 'poster'];

const UNSAFE_URL_PATTERN = /^\s*(javascript|vbscript|data|blob|file)\s*:/i;

/** Escapes a value so it can be embedded in markup as literal text. */
export function escapeHtml(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value).replace(/[&<>"']/g, (char) => HTML_ESCAPE_MAP[char]);
}

/** Escapes a value so it can be embedded in a `RegExp` without changing its meaning. */
export function escapeRegExp(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Removes scripting constructs from an HTML fragment while keeping presentational markup.
 * Parsing happens in an inert document, so nothing is executed or fetched during sanitization.
 */
export function sanitizeHtml(html: unknown): string {
  if (html === null || html === undefined) {
    return '';
  }

  const markup = String(html);
  if (markup.trim() === '') {
    return '';
  }

  const parsed = new DOMParser().parseFromString(markup, 'text/html');
  const body = parsed.body;

  FORBIDDEN_TAGS.forEach((tag) => {
    body.querySelectorAll(tag).forEach((element) => element.remove());
  });

  body.querySelectorAll('*').forEach((element) => {
    Array.from(element.attributes).forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      const value = attribute.value;

      if (name.startsWith('on') || FORBIDDEN_ATTRIBUTES.includes(name)) {
        element.removeAttribute(attribute.name);
        return;
      }

      if (URL_ATTRIBUTES.includes(name) && UNSAFE_URL_PATTERN.test(value)) {
        element.removeAttribute(attribute.name);
        return;
      }

      if (name === 'style' && /expression\s*\(|url\s*\(\s*['"]?\s*javascript:/i.test(value)) {
        element.removeAttribute(attribute.name);
      }
    });
  });

  return body.innerHTML;
}

/** Sanitizes a fragment and assigns it to an element, so callers never touch `innerHTML` directly. */
export function setSanitizedHtml(element: HTMLElement | null | undefined, html: unknown): void {
  if (!element) {
    return;
  }
  element.innerHTML = sanitizeHtml(html);
}

/**
 * Wraps every occurrence of the search terms with `wrapperTag` without trusting the input.
 * Only text nodes are touched, so markup and attributes cannot be broken apart by a crafted term.
 */
export function highlightHtmlTextNodes(
  html: unknown,
  searchTerms: string[],
  wrapperTag: string,
  wrapperClass: string
): string {
  const sanitized = sanitizeHtml(html);
  const terms = searchTerms.map((term) => escapeRegExp(term)).filter((term) => term.length > 0);

  if (sanitized === '' || terms.length === 0) {
    return sanitized;
  }

  const regex = new RegExp(`(${terms.join('|')})`, 'gi');
  const parsed = new DOMParser().parseFromString(sanitized, 'text/html');
  const walker = parsed.createTreeWalker(parsed.body, NodeFilter.SHOW_TEXT);
  const matches: Text[] = [];

  let current = walker.nextNode();
  while (current) {
    matches.push(current as Text);
    current = walker.nextNode();
  }

  matches.forEach((textNode) => {
    const text = textNode.nodeValue ?? '';
    if (!regex.test(text)) {
      regex.lastIndex = 0;
      return;
    }
    regex.lastIndex = 0;

    const fragment = parsed.createDocumentFragment();
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        fragment.appendChild(parsed.createTextNode(text.slice(lastIndex, match.index)));
      }
      const wrapper = parsed.createElement(wrapperTag);
      if (wrapperClass) {
        wrapper.className = wrapperClass;
      }
      wrapper.appendChild(parsed.createTextNode(match[0]));
      fragment.appendChild(wrapper);
      lastIndex = match.index + match[0].length;

      if (match[0].length === 0) {
        regex.lastIndex++;
      }
    }

    if (lastIndex < text.length) {
      fragment.appendChild(parsed.createTextNode(text.slice(lastIndex)));
    }

    textNode.parentNode?.replaceChild(fragment, textNode);
  });

  return parsed.body.innerHTML;
}
